/*
 * Чистая математика экрана звонка: без DOM и без React, чтобы её можно было
 * проверить тестами и переиспользовать в групповом звонке.
 */

import type { CallLayout } from './callRoom';

export type Size = { w: number; h: number };
export type Corner = 'tl' | 'tr' | 'bl' | 'br';

/** Ближайший угол для окошка, отпущенного с левым верхним углом в (x, y). */
export function snapCorner(x: number, y: number, box: Size, bounds: Size): Corner {
  const cx = x + box.w / 2;
  const cy = y + box.h / 2;
  const right = cx >= bounds.w / 2;
  const bottom = cy >= bounds.h / 2;
  return `${bottom ? 'b' : 't'}${right ? 'r' : 'l'}` as Corner;
}

/** Где стоит окошко в заданном углу с отступом от краёв. */
export function cornerPosition(
  corner: Corner,
  box: Size,
  bounds: Size,
  margin = 12,
): { x: number; y: number } {
  return {
    x: corner.endsWith('r') ? bounds.w - box.w - margin : margin,
    y: corner.startsWith('b') ? bounds.h - box.h - margin : margin,
  };
}

export type ShareInput = {
  /** Устройство умеет показывать экран (getDisplayMedia есть). */
  supported: boolean;
  /** Звонок активен (не входящий/исходящий/завершённый). */
  active: boolean;
  /** Право показа сейчас у меня. */
  iAmPresenter: boolean;
  /** Собеседник уже показывает экран. */
  peerScreen: boolean;
  /** Я показываю экран. */
  screenOn: boolean;
  peerName: string;
};

/** Подсказка на кнопке «Показать экран» — та же лестница условий, что раньше жила в CallLayer. */
export function shareHint(i: ShareInput): string {
  if (!i.supported) return '';
  if (!i.active) return 'Звонок ещё не начат';
  if (!i.iAmPresenter) return `Право показа у ${i.peerName}`;
  if (i.peerScreen) return `Экран показывает ${i.peerName}`;
  if (i.screenOn) return 'Остановить показ';
  return 'Показать экран';
}

/**
 * Кому ведущий может дать показ экрана (спека 2026-09-25-guest-screen-share):
 * тем, кто в разговоре, кроме себя и нынешнего держателя, включая гостей. Чей
 * браузер показывать экран не умеет (телефон), в список не попадает.
 */
export function screenShareTargets<T extends { id: string; status: string; screenCapable: boolean }>(
  people: T[],
  hostId: string,
  holderId: string,
): T[] {
  return people.filter((p) => p.status === 'active' && p.screenCapable && p.id !== hostId && p.id !== holderId);
}

/** Можно ли нажать «Показать экран»: устройство умеет, звонок активен, я ведущий, собеседник не показывает. */
export function canShare(i: ShareInput): boolean {
  return i.supported && i.active && i.iAmPresenter && !i.peerScreen;
}


/** Что сетке нужно знать о человеке, чтобы выбрать главную плитку. */
export type Speaker = { id: string; isMe: boolean; speaking: boolean };

/**
 * Кого показывать крупно в раскладке «докладчик». Говорящий важнее всех, но
 * пока молчат — держимся прежнего выбора: иначе плитка прыгала бы на каждой
 * паузе в разговоре. Себя показываем крупно только в одиночестве.
 */
export function mainSpeaker<T extends Speaker>(people: T[], current: string | null): string | null {
  if (!people.length) return null;
  const here = current ? people.find((p) => p.id === current) : null;
  // Пока тот, на кого смотрят, говорит — плитка остаётся за ним. Иначе при
  // разговоре двоих фокус прыгал на того, кто раньше стоит в списке, а не на
  // того, кого слушают (замечание Ильи 15.09.2026).
  if (here?.speaking && !here.isMe) return here.id;
  // свой голос главную плитку не занимает: смотреть на себя незачем
  const loud = people.find((p) => p.speaking && !p.isMe);
  if (loud) return loud.id;
  // Все молчат — держимся прежнего выбора: плитка не должна прыгать в паузах.
  if (here) return here.id;
  return (people.find((p) => !p.isMe) ?? people[0]).id;
}

/**
 * Как назвать звонок одной строкой: «Иван», «Иван и ещё 3». Имена всех
 * участников в полоску не влезают, а первое из них уже говорит, с кем разговор.
 */
export function callTitle(names: string[]): string {
  if (!names.length) return 'Звонок';
  if (names.length === 1) return names[0];
  return `${names[0]} и ещё ${names.length - 1}`;
}

/**
 * Кнопки окна. В полном экране показываем только возврат: две кнопки со
 * стрелками внутрь означали бы разное, и человек не угадает, какая куда.
 * Гостю сворачивать не во что — за его звонком нет приложения, поэтому
 * сворачивания у него не бывает никогда. А вот полный экран гостю нужен
 * так же, как сотруднику.
 */
export function windowButtons(s: { fullscreen: boolean; mini: boolean; isGuest?: boolean }): ('mini' | 'expand' | 'restore')[] {
  if (s.fullscreen) return ['restore'];
  if (s.mini) return ['expand'];
  return s.isGuest ? ['expand'] : ['mini', 'expand'];
}

/**
 * Показывать ли уходящему ведущему выбор преемника. Роль всё равно перейдёт
 * сама — сервер отдаст её вошедшему раньше других, — но ведущий вправе
 * назвать другого. Сотрудников не осталось: передавать некому, гость ведущим
 * не бывает (спека второй порции, раздел 3.1).
 */
export const needsSuccessor = (s: { isHost: boolean; staffLeft: number }) => s.isHost && s.staffLeft > 0;

/**
 * Отсчёт до конца: «14:30», а после нуля — вверх со знаком минус, «−2:05».
 * Звонок по нулю не кончается и никого не выгоняет (решение Ильи): надпись
 * «время вышло» отвергнута как путающая. Знак — типографский минус, не дефис.
 */
export function timerText(endsAt: Date, now: Date): string {
  const left = Math.round((endsAt.getTime() - now.getTime()) / 1000);
  const t = Math.abs(left);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const body = h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  return left < 0 ? `−${body}` : body;
}



/**
 * Показывать ли карточку нового сообщения поверх содержимого. Обычно хватает
 * счётчика на кнопке, но пока человек сам показывает экран или ведёт доску, он
 * смотрит в содержимое и счётчика не видит. Исключение — показ всей рабочей
 * области: карточка попала бы в захват и её увидели бы все (спека, раздел 8).
 */
export const showsMessageToast = (s: {
  chatOpen: boolean;
  screenOn: boolean;
  boardOpen: boolean;
  surface: string | null;
}): boolean => !s.chatOpen && (s.screenOn || s.boardOpen) && s.surface !== 'monitor';

/**
 * Что показывает сцена звонка. Правило собрано в одном месте и покрыто
 * тестами: раньше оно жило россыпью условий в разметке, и одна невнимательная
 * правка ломала экран целиком.
 *
 * Десктоп: раскладку задаёт боковая панель — открыта, значит говорящий крупно
 * и список справа; свёрнута — равные плитки. Телефон: раскладку выбирают
 * кнопкой, а список участников идёт полосой снизу.
 */
export function stageMode(s: {
  mobile: boolean;
  /** Боковая панель десктопа раскрыта (участники или переписка). */
  sideOpen: boolean;
  /** На сцене чужой экран или лист доски. */
  contentActive: boolean;
  /** Сколько человек показывается на сцене. */
  people: number;
  /** Выбор раскладки на телефоне. */
  mobileLayout: CallLayout;
  /** Полоса участников включена кнопкой (только во время показа и доски). */
  stripShown: boolean;
}): { layout: CallLayout; duo: boolean; strip: boolean } {
  /*
   * Разговор вдвоём на компьютере больше не особый случай (решение Ильи
   * 15.09.2026): собеседник во весь экран и своё лицо окошком в углу убраны,
   * вдвоём работает та же мозаика, что и вшестером. Открытая боковая панель
   * по-прежнему переводит сцену на говорящего крупно — там место занято
   * списком участников и перепиской.
   */
  const duo = !s.contentActive && s.people === 2 && s.mobile && s.mobileLayout === 'speaker';
  const layout: CallLayout = s.mobile ? s.mobileLayout : s.sideOpen ? 'speaker' : 'grid';
  const strip = s.contentActive
    ? s.stripShown && s.mobile
    : s.mobile && !duo && s.mobileLayout === 'speaker';
  return { layout, duo, strip };
}

/*
 * Автомикшер (спека 2026-09-25-call-automixer): слушателю громко слышны
 * только говорящие, фон молчащих приглушён. Классический приём
 * конференц-залов, без нейросетей; кто говорит, сообщает медиасервер.
 */
/** С какого числа собеседников включается: вдвоём и втроём не нужен. */
export const MIXER_MIN_OTHERS = 3;
/** Сколько голосов открыто одновременно. */
export const MIXER_OPEN = 2;
/** Столько держится замолчавший: паузы между словами не глушат фразу. */
export const MIXER_HOLD_MS = 1500;
/** Громкость приглушённого: не ноль — первый слог заговорившего не пропадёт. */
export const MIXER_FLOOR = 0.1;

type MixerInput = {
  /** Собеседники в разговоре, без меня. */
  others: string[];
  /** Говорящие сейчас, громкие первыми (порядок медиасервера). */
  speakers: string[];
  /** Когда каждый говорил в последний раз, мс. */
  lastHeard: Record<string, number>;
  now: number;
};

export function mixerGains({ others, speakers, lastHeard, now }: MixerInput): Record<string, number> {
  if (others.length < MIXER_MIN_OTHERS) return Object.fromEntries(others.map((id) => [id, 1]));
  const talking = speakers.filter((id) => others.includes(id));
  const held = others
    .filter((id) => !talking.includes(id) && now - (lastHeard[id] ?? -Infinity) < MIXER_HOLD_MS)
    .sort((a, b) => lastHeard[b] - lastHeard[a]);
  const open = new Set([...talking, ...held].slice(0, MIXER_OPEN));
  return Object.fromEntries(others.map((id) => [id, open.has(id) ? 1 : MIXER_FLOOR]));
}

/** Когда громкости сменятся сами — ближайший конец удержания; null — ждать нечего. */
export function mixerNextChange({ speakers, lastHeard, now }: Omit<MixerInput, 'others'>): number | null {
  const ends = Object.entries(lastHeard)
    .filter(([id, at]) => !speakers.includes(id) && at + MIXER_HOLD_MS > now)
    .map(([, at]) => at + MIXER_HOLD_MS);
  return ends.length ? Math.min(...ends) : null;
}
